import * as Yup from 'yup';
import { useEffect, useMemo, useState } from "react";
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { FormControl, InputLabel, Link, MenuItem, Select, Typography } from "@mui/material";
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import LoadingButton from '@mui/lab/LoadingButton';
// theme
import { bgGradient } from 'src/theme/css';
// hooks
import { useForm } from "react-hook-form";
// routes
import { paths } from 'src/routes/paths';
// components
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import useFetcher, { endpoints } from 'src/utils/axios';
import Iconify from 'src/components/iconify';

type Props = {
  open: boolean;
  onClose: VoidFunction;
  inviteKey: string | null;
  returnTo: string;
  params: string;
};

export default function SalesforceLoginDialog({open, onClose, inviteKey, returnTo, params}: Props) {
  const [organizationType, setOrganizationType] = useState<any>('production');
  const [errorMsg, setErrorMsg] = useState('');
  const [fullCustomDomainUrl, setFullCustomDomainUrl] = useState('');
  const { fetchData } = useFetcher();

  const theme = useTheme();

  const NewUserSchema = useMemo(() =>
      Yup.object().shape({
        custom_domain:
          organizationType === 'custom_domain'
            ? Yup.string().required('Custom Domain is required')
            : Yup.string().notRequired(),
      }),
    [organizationType]
  );

  const defaultValues = useMemo(
    () => ({
      custom_domain: undefined,
    }),
    []
  );

  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    watch,
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      getAuthorisationUrl();
      await new Promise((resolve) => setTimeout(resolve, 500))
    } catch (error) {
      console.error(error);
    }
  });

  const getAuthorisationUrl = () => {
    fetchData([
      endpoints.credential.salesforce_authorization,
      {
        params: {
          custom_domain: fullCustomDomainUrl,
          type: organizationType,
          redirect: 'auth',
          invite_key: inviteKey ?? '',
          return_to: returnTo,
          url_params: params,
        },
      },
    ])
      .then((response: any) => {
        if (/^(ftp|http|https):\/\/[^ "]+$/.test(response)) {
          window.open(response, '_blank');
          window.close();
          onClose();
        } else {
          setErrorMsg(response);
        }
      })
      .catch((response: any) => {
        setErrorMsg(response);
      });
  };

  const customDomain = watch('custom_domain');

  useEffect(() => {
    if (!customDomain) {
      setFullCustomDomainUrl('');
      return;
    }
    let domain = customDomain.trim().replace(/\/+$/, '');
    domain = domain.replace(/^https?:\/\//i, '');
    domain = domain.replace(/\.my\.salesforce\.com$/i, '');
    
    setFullCustomDomainUrl(`https://${domain}.my.salesforce.com`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customDomain]);  

  const renderHeader = (
    <Box
      sx={{
        position: 'relative',
        width: 1,
        height: 140,
        ...bgGradient({
          direction: '135deg',
          startColor: '#2065D1',
          endColor: '#103996',
        }),
      }}
    >
      <Box sx={{ textAlign: "center", p: 3 }}>
        <Iconify icon="logos:salesforce" sx={{ width: "15%", height: "auto" }} />
        <Typography variant="h3" sx={{ fontWeight: "bold", color: "white" }}>
          Login with Salesforce
        </Typography>
      </Box>
    </Box>
  )

  return (
    <Dialog
    fullWidth
    open={open}
    onClose={onClose}
    PaperProps={{
      sx: { maxWidth: 500 },
    }}
  >
    <FormProvider methods={methods} onSubmit={onSubmit}>
      {renderHeader}
      <Box sx={{ p: 5 }}>
        <Stack rowGap={4} columnGap={2} display="grid">
          <FormControl fullWidth >
            <InputLabel id="demo-simple-select-label">Environment Type</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              label="Type"
              value={organizationType}
              onChange={(e) => setOrganizationType(e.target.value as string)}
            >
              <MenuItem value="production">Production</MenuItem>
              <MenuItem value="sandbox">Sandbox</MenuItem>
              <MenuItem value="custom_domain">Custom Domain</MenuItem>
            </Select>
          </FormControl>
          {organizationType === 'custom_domain' && 
            <>
                <RHFTextField
                  name="custom_domain"
                  label="Custom Domain"
                  
                />
                <Box sx={{ ml: 1, bgcolor: 'background.paper', borderRadius: 1, display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2"><strong>Custom Domain Url:</strong></Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{fullCustomDomainUrl || 'Enter your custom domain'}</Typography>
                  </Box>
                </Box>
              {!!errorMsg && (<Alert severity="error">Incorrect Custom Domain</Alert>)}
            </>
          }
        </Stack>
        
        <Box sx={{ display: 'flex', pt: 5 }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <LoadingButton type="submit" variant="contained" loading={isSubmitting} sx={{ backgroundColor: theme.palette.primary.main, "&:hover": { backgroundColor: theme.palette.primary.dark }}}>
            Login
          </LoadingButton>
        </Box>
      </Box>
    </FormProvider>
  </Dialog>
  );
}
